import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus.ts';
import { Checkbox } from '@sorare/core/src/atoms/inputs/Checkbox';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Actions } from '@sorare/core/src/atoms/layout/Actions';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineXS, LabelS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useAddTokensToDeck } from '@sorare/core/src/hooks/decks/useAddTokensToDeck';
import { useDecks } from '@sorare/core/src/hooks/decks/useDecks';
import { useRemoveTokenFromDeck } from '@sorare/core/src/hooks/decks/useRemoveTokenFromDeck';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import type { AddTokenToDeck_anyCard } from './__generated__/index.graphql.ts';

const Body = styled(Vertical)`
  padding: var(--triple-unit);
`;
const Footer = styled(Actions).attrs({ direction: 'horizontal', gap: 2 })`
  position: sticky;
  bottom: 0;
  padding: var(--triple-unit);
`;
const DeckName = styled.div`
  margin-right: auto;
`;

type Props = {
  card: AddTokenToDeck_anyCard;
  onClose: () => void;
  addList: () => void;
};

export const AddTokenToDeck = ({ card, onClose, addList }: Props) => {
  const [query, setQuery] = useState<string>();
  const { decks: defaultDecks, loading } = useDecks({
    sport: card.sport,
    query,
  });
  const [decks, setDecks] = useState(defaultDecks);
  const defaultChecked = card.decks.map(d => d.slug);
  const [checked, setChecked] = useState<string[] | null>(null);
  const addTokensToDeck = useAddTokensToDeck();
  const removeTokenFromDeck = useRemoveTokenFromDeck();
  const { formatMessage } = useIntl();

  const updateDecksCount = (slug: string, amount: number) =>
    setDecks(d =>
      d.map(deck => {
        if (slug === deck.slug) {
          return { ...deck, tokensCount: deck.tokensCount + amount };
        }
        return deck;
      })
    );
  const onCheckboxChange =
    ({ slug }: { slug: string }) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setChecked(c => (c ? [...c, slug] : [slug]));
        updateDecksCount(slug, 1);
      } else {
        setChecked(c => c?.filter(checkedSlug => checkedSlug !== slug) || []);
        updateDecksCount(slug, -1);
      }
    };
  const onConfirm = async () => {
    const [cardToRemoveFrom, cardToAddTo] = decks.reduce<[string[], string[]]>(
      (prev, current) => {
        const deck = defaultDecks.find(({ slug }) => slug === current.slug);
        const toAdd = deck && current.tokensCount > deck.tokensCount;
        const toRemove = deck && current.tokensCount < deck.tokensCount;
        if (toAdd) {
          return [prev[0], [...prev[1], current.slug]];
        }
        if (toRemove) {
          return [[...prev[0], current.slug], prev[1]];
        }
        return prev;
      },
      [[], []]
    );
    await Promise.all([
      ...cardToRemoveFrom.map(async deckSlug =>
        removeTokenFromDeck(deckSlug)(card.slug)
      ),
      ...cardToAddTo.map(async deckSlug =>
        addTokensToDeck(deckSlug)([card.slug])
      ),
    ]);
    onClose();
  };

  if (!checked && defaultChecked?.length) {
    setChecked(defaultChecked);
  }
  if (defaultDecks.length !== decks.length) {
    setDecks(defaultDecks);
  }
  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open
      onClose={onClose}
      title={
        <HeadlineXS as="h6" className="text-center">
          <FormattedMessage {...fantasy.addToList} />
        </HeadlineXS>
      }
      footer={
        <Footer>
          <Button
            startIcon={<FontAwesomeIcon icon={iconPlus} />}
            color="tertiary"
            size="medium"
            onClick={addList}
          >
            <FormattedMessage defaultMessage="New list" />
          </Button>
          <Button color="primary" size="medium" onClick={onConfirm}>
            <FormattedMessage {...glossary.confirm} />
          </Button>
        </Footer>
      }
    >
      <Body>
        <SearchInput
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={formatMessage(glossary.search)}
        />
        {loading && !decks?.length ? (
          <LoadingIndicator small />
        ) : (
          decks?.map(deck => (
            <Horizontal key={deck.slug}>
              <DeckName>{deck.name}</DeckName>
              <LabelS>{deck.tokensCount}</LabelS>
              <Checkbox
                name={deck.name}
                checked={!!checked?.includes(deck.slug)}
                onChange={onCheckboxChange(deck)}
              />
            </Horizontal>
          ))
        )}
      </Body>
    </Dialog>
  );
};

AddTokenToDeck.fragments = {
  anyCard: gql`
    fragment AddTokenToDeck_anyCard on AnyCardInterface {
      slug
      sport
      decks {
        slug
      }
    }
  ` as TypedDocumentNode<AddTokenToDeck_anyCard>,
};
